// /employee/jobs — employee Print Jobs overview (ADR-008 employee surface).
//
// An authorized employee (magic-link signed in, holds the employee JWT) sees
// EVERY print job across organizations in a filterable / sortable / paginated
// table, and can reassign a job to a different contact on the same company
// (e.g. when a project was filed under the wrong person).
//
// This is NOT an impersonation session — it's a direct employee action. The
// page self-guards on the employee token (like EmployeeCustomers) rather than
// <RequireAuth> (which is customer-token auth).

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import type {
  EmployeeJobListItemDto,
  EmployeeJobsQuery,
  EmployeeJobsSortKey,
  PortalCompanyDto,
  QuoteStatus,
  ReassignTargetContactDto,
} from 'shared';

import { extractApiError } from '../api/client.js';
import * as jobsApi from '../api/employeeJobs.js';
import { getEmployeeAccessToken } from '../api/impersonation.js';
import { EmployeeSignOut } from '../components/EmployeeSignOut.js';

const STATUS_OPTIONS: { value: QuoteStatus; label: string }[] = [
  { value: 'draft', label: 'In progress' },
  { value: 'priced', label: 'Priced' },
  { value: 'submitted', label: 'Order placed' },
  { value: 'pending_approval', label: 'Under review' },
  { value: 'approved', label: 'Approved' },
  { value: 'declined', label: 'Declined' },
  { value: 'paid', label: 'Paid' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'deleted', label: 'Deleted' },
];

function humanStatus(status: string): string {
  return STATUS_OPTIONS.find((s) => s.value === status)?.label ?? status;
}

function ownerLabel(job: EmployeeJobListItemDto): string {
  return job.owner_name ?? job.owner_email ?? job.customer_user_id ?? '—';
}

function companyLabel(job: EmployeeJobListItemDto): string {
  return job.company_name ?? job.company_id ?? '—';
}

function formatTotal(job: EmployeeJobListItemDto): string {
  if (!job.total_sale_amount) return '—';
  const n = Number(job.total_sale_amount);
  const amount = Number.isFinite(n)
    ? n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : job.total_sale_amount;
  return `${job.currency ?? 'USD'} ${amount}`;
}

export function EmployeeJobs(): JSX.Element {
  const hasEmployeeToken = Boolean(getEmployeeAccessToken());

  const [filters, setFilters] = useState<EmployeeJobsQuery>({
    sort_by: 'updated_at',
    sort_order: 'desc',
    page: 1,
    limit: 25,
  });
  const [searchInput, setSearchInput] = useState('');
  const [assignJob, setAssignJob] = useState<EmployeeJobListItemDto | null>(null);
  // Per-row delete state: which job is mid-delete (disables its button) and the
  // last delete error (surfaced in a dismissible banner above the table).
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Human-readable labels for the company/owner scope chips — a clicked cell
  // carries an id (for the filter) plus a display label (for the chip), so the
  // chip shows "Acme Co." rather than a raw UUID.
  const [scopeLabels, setScopeLabels] = useState<{ company?: string; owner?: string }>({});

  // Merge a filter patch and reset to page 1 (unless the patch sets page).
  function patchFilters(patch: Partial<EmployeeJobsQuery>): void {
    setFilters((prev) => ({ ...prev, ...patch, page: patch.page ?? 1 }));
  }

  // Debounce the free-text search into the query.
  useEffect(() => {
    const handle = setTimeout(() => {
      patchFilters({ q: searchInput.trim() || undefined });
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['employee-jobs', filters],
    queryFn: () => jobsApi.listJobs(filters),
    placeholderData: keepPreviousData,
    enabled: hasEmployeeToken,
  });

  // Self-guard: bounce to the employee sign-in if the JWT was never issued.
  // (Declared after the hooks so hook order stays stable across renders.)
  if (!hasEmployeeToken) {
    return (
      <div className="card">
        <h2>Sign in required</h2>
        <p>
          Request a magic-link from the <a href="/employee/login">employee sign-in page</a>.
        </p>
      </div>
    );
  }

  function toggleSort(key: EmployeeJobsSortKey): void {
    setFilters((prev) => {
      const sameKey = prev.sort_by === key;
      const nextOrder = sameKey && prev.sort_order === 'desc' ? 'asc' : sameKey ? 'desc' : 'desc';
      return { ...prev, sort_by: key, sort_order: nextOrder, page: 1 };
    });
  }

  function sortIndicator(key: EmployeeJobsSortKey): string {
    if (filters.sort_by !== key) return '';
    return filters.sort_order === 'asc' ? ' ▲' : ' ▼';
  }

  function clearFilters(): void {
    setSearchInput('');
    setScopeLabels({});
    setFilters({ sort_by: 'updated_at', sort_order: 'desc', page: 1, limit: 25 });
  }

  function scopeToCompany(job: EmployeeJobListItemDto): void {
    setScopeLabels((s) => ({ ...s, company: companyLabel(job) }));
    patchFilters({ company_id: job.company_id ?? undefined });
  }

  function scopeToOwner(job: EmployeeJobListItemDto): void {
    setScopeLabels((s) => ({ ...s, owner: ownerLabel(job) }));
    patchFilters({ customer_user_id: job.customer_user_id ?? undefined });
  }

  async function handleDelete(job: EmployeeJobListItemDto): Promise<void> {
    const label = job.project_title ?? 'Untitled job';
    if (
      !confirm(
        `Delete "${label}"? It will be removed from the worklist. This is a soft delete: support can recover it.`,
      )
    ) {
      return;
    }
    setDeletingId(job.id);
    setDeleteError(null);
    try {
      await jobsApi.deleteJob(job.id);
      await refetch();
    } catch (err) {
      setDeleteError(extractApiError(err)?.message ?? 'Could not delete this job.');
    } finally {
      setDeletingId(null);
    }
  }

  const hasActiveFilters =
    Boolean(filters.q) ||
    Boolean(filters.status) ||
    Boolean(filters.company_id) ||
    Boolean(filters.customer_user_id) ||
    Boolean(filters.date_from) ||
    Boolean(filters.date_to);

  const total = data?.pagination.total ?? 0;
  const limit = data?.pagination.limit ?? filters.limit ?? 25;
  const page = data?.pagination.page ?? filters.page ?? 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const sortableHeader = (
    label: string,
    key: EmployeeJobsSortKey,
    align?: 'right',
  ): JSX.Element => (
    <th
      aria-sort={
        filters.sort_by === key
          ? filters.sort_order === 'asc'
            ? 'ascending'
            : 'descending'
          : 'none'
      }
      style={align ? { textAlign: align } : undefined}
    >
      <button
        type="button"
        className="link-button"
        onClick={() => toggleSort(key)}
        style={{ font: 'inherit', fontWeight: 600 }}
      >
        {label}
        <span aria-hidden="true">{sortIndicator(key)}</span>
      </button>
    </th>
  );

  return (
    <div className="card">
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
        <h2>Unassigned print jobs</h2>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, fontSize: 14 }}>
          <Link to="/employee/customers">Customer picker →</Link>
          <EmployeeSignOut />
        </div>
      </div>
      <p style={{ color: 'var(--color-text-muted)' }}>
        Jobs that still need filing: each is missing a company or a contact. Assign one to a
        company and contact already in the portal; once both are set it&apos;s fully attributed and
        drops off this list. (The full all-quotes view lives in the ERP&apos;s opportunities, not
        here.)
      </p>

      {/* ── Filter bar ───────────────────────────────────────────── */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          alignItems: 'flex-end',
          marginTop: 16,
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 220px' }}>
          <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Search</span>
          <input
            type="text"
            value={searchInput}
            maxLength={128}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Quote #, project, owner name/email, company…"
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Status</span>
          <select
            value={filters.status ?? ''}
            onChange={(e) =>
              patchFilters({ status: (e.target.value || undefined) as QuoteStatus | undefined })
            }
          >
            <option value="">All</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Date field</span>
          <select
            value={filters.date_field ?? 'updated_at'}
            onChange={(e) =>
              patchFilters({
                date_field: e.target.value as EmployeeJobsQuery['date_field'],
              })
            }
          >
            <option value="updated_at">Updated</option>
            <option value="created_at">Created</option>
            <option value="requested_finish_date">Requested finish</option>
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>From</span>
          <input
            type="date"
            value={filters.date_from ?? ''}
            onChange={(e) => patchFilters({ date_from: e.target.value || undefined })}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>To</span>
          <input
            type="date"
            value={filters.date_to ?? ''}
            onChange={(e) => patchFilters({ date_to: e.target.value || undefined })}
          />
        </label>
        {hasActiveFilters && (
          <button type="button" onClick={clearFilters}>
            Clear filters
          </button>
        )}
      </div>

      {/* Active scope chips (set by clicking a Company/Owner cell). */}
      {(filters.company_id || filters.customer_user_id) && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          {filters.company_id && (
            <span className="tag">
              Company: {scopeLabels.company ?? filters.company_id}{' '}
              <button
                type="button"
                className="link-button"
                aria-label="Remove company filter"
                onClick={() => {
                  setScopeLabels((s) => ({ ...s, company: undefined }));
                  patchFilters({ company_id: undefined });
                }}
              >
                ✕
              </button>
            </span>
          )}
          {filters.customer_user_id && (
            <span className="tag">
              Owner: {scopeLabels.owner ?? filters.customer_user_id}{' '}
              <button
                type="button"
                className="link-button"
                aria-label="Remove owner filter"
                onClick={() => {
                  setScopeLabels((s) => ({ ...s, owner: undefined }));
                  patchFilters({ customer_user_id: undefined });
                }}
              >
                ✕
              </button>
            </span>
          )}
        </div>
      )}

      {isLoading && <p style={{ marginTop: 16 }}>Loading…</p>}
      {isError && (
        <div className="banner banner--error" style={{ marginTop: 16 }}>
          We couldn&apos;t load print jobs.{' '}
          <button type="button" className="link-button" onClick={() => void refetch()}>
            Retry
          </button>
        </div>
      )}
      {deleteError && (
        <div className="banner banner--error" style={{ marginTop: 16 }}>
          {deleteError}{' '}
          <button
            type="button"
            className="link-button"
            aria-label="Dismiss"
            onClick={() => setDeleteError(null)}
          >
            ✕
          </button>
        </div>
      )}

      {data && (
        <>
          <table
            className="quote-table"
            aria-busy={isFetching}
            style={{ marginTop: 16, opacity: isFetching ? 0.6 : 1 }}
          >
            <thead>
              <tr>
                <th>Quote #</th>
                {sortableHeader('Project', 'project_title')}
                <th>Owner</th>
                <th>Company</th>
                {sortableHeader('Status', 'status')}
                {sortableHeader('Total', 'total_sale_amount', 'right')}
                {sortableHeader('Updated', 'updated_at')}
                <th />
              </tr>
            </thead>
            <tbody>
              {data.jobs.map((job) => (
                <tr key={job.id}>
                  <td style={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {job.legacy_quote_id ?? '—'}
                  </td>
                  <td>{job.project_title ?? 'Untitled job'}</td>
                  <td>
                    {job.customer_user_id ? (
                      <button
                        type="button"
                        className="link-button"
                        title="Filter by this owner"
                        onClick={() => scopeToOwner(job)}
                      >
                        {ownerLabel(job)}
                      </button>
                    ) : (
                      ownerLabel(job)
                    )}
                    {job.owner_email && job.owner_name && (
                      <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
                        {job.owner_email}
                      </div>
                    )}
                  </td>
                  <td>
                    {job.company_id ? (
                      <button
                        type="button"
                        className="link-button"
                        title="Filter by this company"
                        onClick={() => scopeToCompany(job)}
                      >
                        {companyLabel(job)}
                      </button>
                    ) : (
                      companyLabel(job)
                    )}
                  </td>
                  <td>{humanStatus(job.status)}</td>
                  <td style={{ textAlign: 'right' }}>{formatTotal(job)}</td>
                  <td>{new Date(job.updated_at).toLocaleDateString()}</td>
                  <td className="actions">
                    <button type="button" onClick={() => setAssignJob(job)}>
                      Assign
                    </button>
                    <button
                      type="button"
                      className="danger"
                      disabled={deletingId === job.id}
                      onClick={() => void handleDelete(job)}
                    >
                      {deletingId === job.id ? 'Deleting…' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
              {data.jobs.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', color: 'var(--color-text-muted)' }}>
                    {hasActiveFilters
                      ? 'No unassigned jobs match these filters.'
                      : 'Every job is filed under a company and contact. Nothing to assign.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* ── Pagination ─────────────────────────────────────────── */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginTop: 12,
              gap: 12,
            }}
          >
            <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
              {total === 0
                ? 'No jobs'
                : `${(page - 1) * limit + 1}–${Math.min(page * limit, total)} of ${total}`}
            </span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                aria-label="Previous page"
                disabled={page <= 1 || isFetching}
                onClick={() => patchFilters({ page: page - 1 })}
              >
                ← Prev
              </button>
              <span style={{ fontSize: 13 }}>
                Page {page} / {totalPages}
              </span>
              <button
                type="button"
                aria-label="Next page"
                disabled={page >= totalPages || isFetching}
                onClick={() => patchFilters({ page: page + 1 })}
              >
                Next →
              </button>
            </div>
          </div>
        </>
      )}

      {assignJob && (
        <AssignModal
          job={assignJob}
          onClose={() => setAssignJob(null)}
          onAssigned={() => {
            setAssignJob(null);
            void refetch();
          }}
        />
      )}
    </div>
  );
}

// ── Assign modal ────────────────────────────────────────────────────
// Files a job under a company + contact. Two shapes:
//   - Job already has a company → pick a contact on that company (Nexus).
//   - Orphan job (no company yet) → first pick a company the PORTAL already
//     knows (GET /employee/companies — never a Nexus directory search), then
//     pick a contact on it. The reassign write adopts the chosen company.
function AssignModal({
  job,
  onClose,
  onAssigned,
}: {
  job: EmployeeJobListItemDto;
  onClose: () => void;
  onAssigned: () => void;
}): JSX.Element {
  // Company step (orphan jobs only): the portal company the employee picks.
  const [chosenCompany, setChosenCompany] = useState<PortalCompanyDto | null>(null);
  const [companySearch, setCompanySearch] = useState('');
  const [debouncedCompanySearch, setDebouncedCompanySearch] = useState('');

  const [contactSearch, setContactSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // The company the contact step works against. A filed job is locked to its
  // own company; an orphan adopts the one the employee picks. While it's null
  // we're on the company-picking step.
  const effectiveCompanyId = job.company_id ?? chosenCompany?.company_id ?? null;
  const onCompanyStep = !effectiveCompanyId;

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(contactSearch), 300);
    return () => clearTimeout(handle);
  }, [contactSearch]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedCompanySearch(companySearch), 300);
    return () => clearTimeout(handle);
  }, [companySearch]);

  // Move focus into the dialog (search box if present, else the close button)
  // so keyboard users aren't left behind the backdrop. Re-runs when the step
  // changes so picking a company lands focus on the contact search.
  useEffect(() => {
    const node = dialogRef.current;
    if (!node) return;
    const target = node.querySelector<HTMLElement>(
      'input:not([disabled]), button:not([disabled]), a[href]',
    );
    (target ?? node).focus();
  }, [onCompanyStep]);

  // Escape to dismiss + a focus trap so Tab cycles within the dialog rather
  // than escaping to the dimmed page behind it.
  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>): void {
    if (e.key === 'Escape') {
      onClose();
      return;
    }
    if (e.key !== 'Tab') return;
    const node = dialogRef.current;
    if (!node) return;
    const focusables = Array.from(
      node.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((el) => el.offsetParent !== null);
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (!first || !last) return;
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // Portal companies to file an orphan job under — only fetched while on the
  // company step (a filed job skips it entirely).
  const companiesQuery = useQuery({
    queryKey: ['employee-portal-companies', debouncedCompanySearch],
    queryFn: () => jobsApi.listPortalCompanies(debouncedCompanySearch),
    enabled: onCompanyStep,
  });

  const contactsQuery = useQuery({
    queryKey: ['employee-job-contacts', job.id, effectiveCompanyId, debouncedSearch],
    queryFn: () =>
      jobsApi.searchJobContacts(job.id, debouncedSearch, effectiveCompanyId ?? undefined),
    enabled: Boolean(effectiveCompanyId),
  });

  function onPick(contact: ReassignTargetContactDto): void {
    setSubmitting(contact.contact_id);
    setErrorMessage(null);
    void (async () => {
      try {
        await jobsApi.reassignJob(job.id, {
          target_contact_id: contact.contact_id,
          target_company_id: contact.company_id,
        });
        onAssigned();
      } catch (err) {
        setErrorMessage(extractApiError(err)?.message ?? 'Could not assign this job.');
        setSubmitting(null);
      }
    })();
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        // Scrim: black @ 40% over the page. Black is the body-text color; an
        // overlay alpha isn't part of the token scale, so it's expressed inline.
        background: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--space-4)',
        zIndex: 100,
      }}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Assign job"
        aria-describedby="assign-job-context"
        tabIndex={-1}
        className="card"
        style={{ maxWidth: 520, width: '100%', maxHeight: '80vh', overflowY: 'auto' }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
          <h3 style={{ margin: 0 }}>Assign job</h3>
          <button type="button" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <p id="assign-job-context" style={{ color: 'var(--color-text-muted)', marginTop: 8 }}>
          <strong>{job.project_title ?? 'Untitled job'}</strong>
          <br />
          Currently: {ownerLabel(job)} · {companyLabel(job)}
        </p>

        {onCompanyStep ? (
          // ── Step 1 (orphan jobs): pick a company already in the portal ──
          <>
            <p style={{ marginTop: 12 }}>
              This job isn&apos;t filed under a company yet. Pick a company already in the portal to
              file it under, then choose a contact.
            </p>
            <input
              type="text"
              value={companySearch}
              maxLength={128}
              onChange={(e) => setCompanySearch(e.target.value)}
              placeholder="Search companies in the portal…"
              style={{ width: '100%', marginTop: 12 }}
            />
            {companiesQuery.isLoading && <p style={{ marginTop: 12 }}>Loading companies…</p>}
            {companiesQuery.isError && (
              <div className="banner banner--error" style={{ marginTop: 12 }}>
                Could not load companies.
              </div>
            )}
            {companiesQuery.data && companiesQuery.data.length === 0 && (
              <p style={{ marginTop: 12 }}>
                {debouncedCompanySearch
                  ? 'No portal companies match that search.'
                  : 'No companies in the portal yet.'}
              </p>
            )}
            {companiesQuery.data && companiesQuery.data.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, marginTop: 12 }}>
                {companiesQuery.data.map((c) => (
                  <li
                    key={c.company_id}
                    style={{
                      padding: '10px 12px',
                      borderTop: '1px solid var(--color-border)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: 600 }}>{c.company_name ?? c.company_id}</div>
                      <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                        {c.job_count} {c.job_count === 1 ? 'job' : 'jobs'}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="primary"
                      onClick={() => {
                        setChosenCompany(c);
                        setContactSearch('');
                        setDebouncedSearch('');
                        setErrorMessage(null);
                      }}
                    >
                      Choose
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        ) : (
          // ── Step 2: pick a contact on the (filed or just-chosen) company ──
          <>
            {!job.company_id && chosenCompany && (
              <div
                style={{
                  marginTop: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                }}
              >
                <span style={{ fontSize: 14 }}>
                  Filing under{' '}
                  <strong>{chosenCompany.company_name ?? chosenCompany.company_id}</strong>
                </span>
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setChosenCompany(null);
                    setErrorMessage(null);
                  }}
                >
                  ← Change company
                </button>
              </div>
            )}
            <input
              type="text"
              value={contactSearch}
              maxLength={128}
              onChange={(e) => setContactSearch(e.target.value)}
              placeholder="Search contacts on this company…"
              style={{ width: '100%', marginTop: 12 }}
            />
            {errorMessage && (
              <div className="banner banner--error" style={{ marginTop: 12 }}>
                {errorMessage}
              </div>
            )}
            {contactsQuery.isLoading && <p style={{ marginTop: 12 }}>Loading contacts…</p>}
            {contactsQuery.isError && (
              <div className="banner banner--error" style={{ marginTop: 12 }}>
                Could not load contacts for this company.
              </div>
            )}
            {contactsQuery.data && contactsQuery.data.length === 0 && (
              <p style={{ marginTop: 12 }}>No contacts found.</p>
            )}
            {contactsQuery.data && contactsQuery.data.length > 0 && (
              <ul style={{ listStyle: 'none', padding: 0, marginTop: 12 }}>
                {contactsQuery.data.map((c) => {
                  const isCurrent = c.contact_id === job.contact_id;
                  return (
                    <li
                      key={c.contact_id}
                      style={{
                        padding: '10px 12px',
                        borderTop: '1px solid var(--color-border)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600 }}>{c.name ?? c.email ?? c.contact_id}</div>
                        {c.email && (
                          <div style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{c.email}</div>
                        )}
                      </div>
                      <button
                        type="button"
                        className="primary"
                        disabled={submitting !== null || isCurrent}
                        onClick={() => onPick(c)}
                      >
                        {isCurrent
                          ? 'Current'
                          : submitting === c.contact_id
                            ? 'Assigning…'
                            : 'Assign'}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}
